import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);

const applyThemeClass = (theme) => {
  document.documentElement.classList.toggle("dark", theme === "dark");
};

export const ThemeProvider = ({ children }) => {
  const { user, API } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem("nova-theme") || "light");
  const syncedUserIdRef = useRef(null);

  useEffect(() => {
    applyThemeClass(theme);
    localStorage.setItem("nova-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!user) {
      syncedUserIdRef.current = null;
      return;
    }
    if (user.theme_preference && syncedUserIdRef.current !== user.user_id) {
      syncedUserIdRef.current = user.user_id;
      setTheme(user.theme_preference);
    }
  }, [user]);

  const setThemeAndPersist = useCallback(
    (next) => {
      setTheme(next);
      if (user) {
        axios.patch(`${API}/auth/theme`, { theme: next }, { withCredentials: true }).catch(() => {});
      }
    },
    [user, API]
  );

  const toggleTheme = useCallback(() => {
    setThemeAndPersist(theme === "dark" ? "light" : "dark");
  }, [theme, setThemeAndPersist]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeAndPersist, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
